// TG EDIT: scratch to win popup, built on the theme's modal
await customElements.whenDefined("x-modal");

class ScratchCard extends HTMLElement {
  #canvas;
  #context;
  #lastPoint = null;
  #moves = 0;
  #abortController;
  #resizeObserver;

  connectedCallback() {
    this.#canvas = this.querySelector("canvas");
    this.#context = this.#canvas.getContext("2d", { willReadFrequently: true });
    this.#abortController = new AbortController();
    this.#resizeObserver = new ResizeObserver(this.#drawFoil.bind(this));

    const listenerOptions = { signal: this.#abortController.signal };
    this.#canvas.addEventListener("pointerdown", this.#onPointerDown.bind(this), listenerOptions);
    this.#canvas.addEventListener("pointermove", this.#onPointerMove.bind(this), listenerOptions);
    this.#canvas.addEventListener("pointerup", this.#onPointerUp.bind(this), listenerOptions);
    this.#canvas.addEventListener("pointercancel", this.#onPointerUp.bind(this), listenerOptions);
    this.#canvas.addEventListener("keydown", this.#onKeyDown.bind(this), listenerOptions);
    this.#resizeObserver.observe(this.#canvas);
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#resizeObserver.disconnect();
  }

  reveal() {
    if (this.hasAttribute("revealed")) {
      return;
    }

    this.setAttribute("revealed", "");
    this.dispatchEvent(new CustomEvent("scratch-card:reveal", { bubbles: true }));
    this.#canvas.inert = true;
  }

  #drawFoil() {
    const { width, height } = this.#canvas.getBoundingClientRect();

    if (width === 0 || this.hasAttribute("revealed")) {
      return;
    }

    const ratio = window.devicePixelRatio, style = getComputedStyle(this);
    this.#canvas.width = width * ratio;
    this.#canvas.height = height * ratio;

    const gradient = this.#context.createLinearGradient(0, 0, this.#canvas.width, this.#canvas.height);
    gradient.addColorStop(0, "#d4d4d4");
    gradient.addColorStop(0.5, "#a6a6a6");
    gradient.addColorStop(1, "#d4d4d4");

    this.#context.globalCompositeOperation = "source-over";
    this.#context.fillStyle = gradient;
    this.#context.fillRect(0, 0, this.#canvas.width, this.#canvas.height);

    this.#context.font = `${style.getPropertyValue("--heading-font-weight")} ${Math.round(this.#canvas.height * 0.22)}px ${style.getPropertyValue("--heading-font-family")}`;
    this.#context.fillStyle = "#666";
    this.#context.textAlign = "center";
    this.#context.textBaseline = "middle";
    this.#context.fillText(this.#canvas.getAttribute("aria-label").toUpperCase(), this.#canvas.width / 2, this.#canvas.height / 2);

    this.#context.globalCompositeOperation = "destination-out";
    this.#context.lineWidth = this.#canvas.height * 0.3;
    this.#context.lineCap = "round";
    this.#context.lineJoin = "round";
  }

  #onPointerDown(event) {
    this.#canvas.setPointerCapture(event.pointerId);
    this.#lastPoint = this.#getPoint(event);
  }

  #onPointerMove(event) {
    if (!this.#lastPoint) {
      return;
    }

    const point = this.#getPoint(event);
    this.#context.beginPath();
    this.#context.moveTo(...this.#lastPoint);
    this.#context.lineTo(...point);
    this.#context.stroke();
    this.#lastPoint = point;

    if (++this.#moves % 10 === 0) {
      this.#checkProgress();
    }
  }

  #onPointerUp() {
    this.#lastPoint = null;
    this.#checkProgress();
  }

  #onKeyDown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.reveal();
    }
  }

  #getPoint(event) {
    const rect = this.#canvas.getBoundingClientRect(), ratio = this.#canvas.width / rect.width;
    return [(event.clientX - rect.left) * ratio, (event.clientY - rect.top) * ratio];
  }

  #checkProgress() {
    const { data } = this.#context.getImageData(0, 0, this.#canvas.width, this.#canvas.height);
    let cleared = 0;

    // Only check the alpha of every 16th pixel
    for (let i = 3; i < data.length; i += 64) {
      cleared += data[i] === 0;
    }

    if (cleared / (data.length / 64) > 0.5) {
      this.reveal();
    }
  }
}

class ScratchPopup extends customElements.get("x-modal") {
  #scheduled = false;

  connectedCallback() {
    super.connectedCallback();

    const claimButton = this.querySelector("[data-claim]");
    this.addEventListener("scratch-card:reveal", (event) => {
      claimButton.hidden = false;

      if (event.target.querySelector(":focus-visible")) {
        claimButton.focus();
      }
    }, { signal: this.abortController.signal });
    claimButton.addEventListener("click", this.#showOffer.bind(this), { signal: this.abortController.signal });

    // The modal is moved to the body when it opens, which runs this callback again
    if (!this.#scheduled && this.shouldAppearAutomatically) {
      this.#scheduled = true;
      setTimeout(() => this.show(), parseInt(this.getAttribute("apparition-delay") || 0) * 1e3);
    }
  }

  get shouldAppearAutomatically() {
    return !(localStorage.getItem("theme:popup-filled") === "true" || this.hasAttribute("only-once") && localStorage.getItem("theme:scratch-popup-appeared") === "true");
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);

    if (name === "open" && this.open) {
      localStorage.setItem("theme:scratch-popup-appeared", "true");
    }
  }

  #showOffer() {
    const [scratchStep, offerStep] = this.querySelectorAll(".scratch-popup__step");
    scratchStep.hidden = true;
    offerStep.hidden = false;
    offerStep.querySelector('input[type="email"]')?.focus();
  }
}

if (!window.customElements.get("scratch-card")) {
  window.customElements.define("scratch-card", ScratchCard);
}

if (!window.customElements.get("scratch-popup")) {
  window.customElements.define("scratch-popup", ScratchPopup);
}
